import React, { useRef, useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, Text, RoundedBox, Environment } from '@react-three/drei';
import * as THREE from 'three';

const FLOOR_THEMES = {
  wood: { floor: '#c4a77d', gridMajor: '#a08060', gridMinor: '#b09070', skyTop: '#1e3a5f', skyBottom: '#0f172a' },
  tile: { floor: '#dbe4ec', gridMajor: '#94a3b8', gridMinor: '#cbd5e1', skyTop: '#475569', skyBottom: '#0f172a' },
  carpet: { floor: '#334155', gridMajor: '#475569', gridMinor: '#1e293b', skyTop: '#1e293b', skyBottom: '#020617' },
  marble: { floor: '#f1f5f9', gridMajor: '#cbd5e1', gridMinor: '#e2e8f0', skyTop: '#94a3b8', skyBottom: '#1e293b' },
  concrete: { floor: '#6b7280', gridMajor: '#4b5563', gridMinor: '#9ca3af', skyTop: '#374151', skyBottom: '#111827' },
  grass: { floor: '#22c55e', gridMajor: '#15803d', gridMinor: '#4ade80', skyTop: '#14532d', skyBottom: '#052e16' },
  neon: { floor: '#111827', gridMajor: '#8b5cf6', gridMinor: '#ec4899', skyTop: '#312e81', skyBottom: '#020617' },
};

const WALL_THEMES = {
  warm: { back: '#d4c4a4', left: '#c9b994', trim: '#8B5A2B' },
  studio: { back: '#e2e8f0', left: '#cbd5e1', trim: '#64748b' },
  moss: { back: '#65a30d', left: '#4d7c0f', trim: '#365314' },
  neon: { back: '#312e81', left: '#1e1b4b', trim: '#7c3aed' },
  sunset: { back: '#fdba74', left: '#fb923c', trim: '#ea580c' },
};

const SCENERY_THEMES = {
  losAngeles: {
    skyTop: '#f59e0b',
    skyBottom: '#f97316',
    haze: '#fde68a',
  },
  miami: {
    skyTop: '#38bdf8',
    skyBottom: '#f472b6',
    haze: '#bae6fd',
  },
  thailand: {
    skyTop: '#22c55e',
    skyBottom: '#0ea5e9',
    haze: '#fde68a',
  },
};

// ============================================
// 3D FURNITURE COMPONENTS
// ============================================

const Desk = ({ position, isActive = false }) => {
  return (
    <group position={position}>
      {/* Desktop */}
      <RoundedBox args={[1.2, 0.08, 0.7]} position={[0, 0.75, 0]} radius={0.02}>
        <meshStandardMaterial color={isActive ? "#4ade80" : "#8B5A2B"} />
      </RoundedBox>
      {/* Legs */}
      {[[-0.5, 0, -0.25], [0.5, 0, -0.25], [-0.5, 0, 0.25], [0.5, 0, 0.25]].map((pos, i) => (
        <mesh key={i} position={[pos[0], 0.35, pos[2]]}>
          <cylinderGeometry args={[0.03, 0.03, 0.7]} />
          <meshStandardMaterial color="#5C4033" />
        </mesh>
      ))}
      {/* Monitor */}
      <group position={[0, 1.05, -0.15]}>
        <RoundedBox args={[0.5, 0.35, 0.03]} radius={0.01}>
          <meshStandardMaterial color="#1f2937" />
        </RoundedBox>
        <mesh position={[0, 0, 0.02]}>
          <planeGeometry args={[0.45, 0.28]} />
          <meshStandardMaterial color={isActive ? "#4ade80" : "#38bdf8"} emissive={isActive ? "#4ade80" : "#38bdf8"} emissiveIntensity={0.3} />
        </mesh>
        {/* Stand */}
        <mesh position={[0, -0.25, 0.05]}>
          <boxGeometry args={[0.08, 0.15, 0.08]} />
          <meshStandardMaterial color="#374151" />
        </mesh>
      </group>
      {/* Keyboard */}
      <RoundedBox args={[0.35, 0.02, 0.12]} position={[0, 0.8, 0.15]} radius={0.005}>
        <meshStandardMaterial color="#1f2937" />
      </RoundedBox>
      {/* Active glow */}
      {isActive && (
        <pointLight position={[0, 1.2, 0]} color="#4ade80" intensity={0.5} distance={2} />
      )}
    </group>
  );
};

const Chair = ({ position, color = "#6366f1" }) => {
  return (
    <group position={position}>
      {/* Seat */}
      <RoundedBox args={[0.45, 0.08, 0.45]} position={[0, 0.45, 0]} radius={0.02}>
        <meshStandardMaterial color={color} />
      </RoundedBox>
      {/* Back */}
      <RoundedBox args={[0.45, 0.5, 0.08]} position={[0, 0.8, -0.2]} radius={0.02}>
        <meshStandardMaterial color={color} />
      </RoundedBox>
      {/* Base */}
      <mesh position={[0, 0.2, 0]}>
        <cylinderGeometry args={[0.03, 0.03, 0.4]} />
        <meshStandardMaterial color="#374151" />
      </mesh>
      {/* Wheels base */}
      <mesh position={[0, 0.05, 0]}>
        <cylinderGeometry args={[0.2, 0.2, 0.03]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
    </group>
  );
};

const Plant = ({ position, large = false }) => {
  const scale = large ? 1.3 : 1;
  return (
    <group position={position} scale={scale}>
      {/* Pot */}
      <mesh position={[0, 0.15, 0]}>
        <cylinderGeometry args={[0.15, 0.12, 0.3]} />
        <meshStandardMaterial color="#92400e" />
      </mesh>
      {/* Foliage */}
      <mesh position={[0, 0.5, 0]}>
        <sphereGeometry args={[0.25, 16, 16]} />
        <meshStandardMaterial color="#22c55e" />
      </mesh>
      <mesh position={[0.1, 0.6, 0.1]}>
        <sphereGeometry args={[0.18, 16, 16]} />
        <meshStandardMaterial color="#16a34a" />
      </mesh>
    </group>
  );
};

const Sofa = ({ position }) => {
  return (
    <group position={position}>
      {/* Base */}
      <RoundedBox args={[1.8, 0.35, 0.7]} position={[0, 0.25, 0]} radius={0.05}>
        <meshStandardMaterial color="#a78bfa" />
      </RoundedBox>
      {/* Back */}
      <RoundedBox args={[1.8, 0.4, 0.15]} position={[0, 0.55, -0.28]} radius={0.05}>
        <meshStandardMaterial color="#8b5cf6" />
      </RoundedBox>
      {/* Arms */}
      <RoundedBox args={[0.15, 0.3, 0.6]} position={[-0.85, 0.4, 0]} radius={0.03}>
        <meshStandardMaterial color="#8b5cf6" />
      </RoundedBox>
      <RoundedBox args={[0.15, 0.3, 0.6]} position={[0.85, 0.4, 0]} radius={0.03}>
        <meshStandardMaterial color="#8b5cf6" />
      </RoundedBox>
      {/* Cushions */}
      <RoundedBox args={[0.55, 0.1, 0.5]} position={[-0.4, 0.45, 0.05]} radius={0.03}>
        <meshStandardMaterial color="#c4b5fd" />
      </RoundedBox>
      <RoundedBox args={[0.55, 0.1, 0.5]} position={[0.4, 0.45, 0.05]} radius={0.03}>
        <meshStandardMaterial color="#c4b5fd" />
      </RoundedBox>
    </group>
  );
};

const CoffeeMachine = ({ position }) => {
  return (
    <group position={position}>
      <RoundedBox args={[0.3, 0.5, 0.25]} position={[0, 0.25, 0]} radius={0.02}>
        <meshStandardMaterial color="#334155" />
      </RoundedBox>
      {/* Screen */}
      <mesh position={[0, 0.35, 0.13]}>
        <planeGeometry args={[0.15, 0.08]} />
        <meshStandardMaterial color="#22c55e" emissive="#22c55e" emissiveIntensity={0.5} />
      </mesh>
      {/* Dispenser */}
      <mesh position={[0, 0.1, 0.1]}>
        <boxGeometry args={[0.12, 0.15, 0.08]} />
        <meshStandardMaterial color="#1e293b" />
      </mesh>
    </group>
  );
};

const ServerRack = ({ position }) => {
  return (
    <group position={position}>
      <RoundedBox args={[0.4, 1.2, 0.35]} position={[0, 0.6, 0]} radius={0.02}>
        <meshStandardMaterial color="#111827" />
      </RoundedBox>
      {/* Server units */}
      {[0.9, 0.7, 0.5, 0.3].map((y, i) => (
        <group key={i}>
          <mesh position={[0, y, 0.18]}>
            <boxGeometry args={[0.35, 0.12, 0.02]} />
            <meshStandardMaterial color="#334155" />
          </mesh>
          {/* LEDs */}
          <mesh position={[0.12, y, 0.19]}>
            <sphereGeometry args={[0.015]} />
            <meshStandardMaterial color="#4ade80" emissive="#4ade80" emissiveIntensity={1} />
          </mesh>
          <mesh position={[0.08, y, 0.19]}>
            <sphereGeometry args={[0.015]} />
            <meshStandardMaterial color="#38bdf8" emissive="#38bdf8" emissiveIntensity={0.5} />
          </mesh>
        </group>
      ))}
    </group>
  );
};

const Window3D = ({ position }) => {
  return (
    <group position={position}>
      <RoundedBox args={[0.8, 0.6, 0.05]} radius={0.02}>
        <meshStandardMaterial color="#e0f2fe" transparent opacity={0.6} />
      </RoundedBox>
      {/* Frame */}
      <mesh position={[0, 0, 0.03]}>
        <boxGeometry args={[0.82, 0.02, 0.02]} />
        <meshStandardMaterial color="#f8fafc" />
      </mesh>
      <mesh position={[0, 0, 0.03]} rotation={[0, 0, Math.PI / 2]}>
        <boxGeometry args={[0.62, 0.02, 0.02]} />
        <meshStandardMaterial color="#f8fafc" />
      </mesh>
    </group>
  );
};

const Rug = ({ position, width = 2.5, depth = 1.5 }) => {
  return (
    <mesh position={position} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[width, depth]} />
      <meshStandardMaterial color="#8b5cf6" transparent opacity={0.3} />
    </mesh>
  );
};

const PalmTree = ({ position, swayOffset = 0, height = 1.8 }) => {
  const groupRef = useRef();

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.8 + swayOffset) * 0.08;
    }
  });

  return (
    <group ref={groupRef} position={position}>
      <mesh position={[0, height / 2, 0]}>
        <cylinderGeometry args={[0.06, 0.09, height, 10]} />
        <meshStandardMaterial color="#8b5a2b" />
      </mesh>
      {[0, 1, 2, 3, 4].map((index) => (
        <mesh
          key={index}
          position={[0, height, 0]}
          rotation={[0.1, (index / 5) * Math.PI * 2, index % 2 === 0 ? 0.35 : -0.35]}
        >
          <coneGeometry args={[0.14, 0.8, 6]} />
          <meshStandardMaterial color={index % 2 === 0 ? '#16a34a' : '#22c55e'} />
        </mesh>
      ))}
    </group>
  );
};

const SkylineBuilding = ({ position, size = [0.6, 1.8, 0.5], color = '#1e293b', glow = '#38bdf8' }) => (
  <group position={position}>
    <RoundedBox args={size} radius={0.03}>
      <meshStandardMaterial color={color} />
    </RoundedBox>
    {[-0.18, 0, 0.18].map((x, row) => (
      [-0.28, 0, 0.28].map((y, col) => (
        <mesh key={`${row}-${col}`} position={[x, y, size[2] / 2 + 0.01]}>
          <planeGeometry args={[0.08, 0.14]} />
          <meshStandardMaterial color={glow} emissive={glow} emissiveIntensity={0.35} />
        </mesh>
      ))
    ))}
  </group>
);

const Car = ({ basePosition, color = '#ef4444', speed = 0.6, range = 3.4 }) => {
  const groupRef = useRef();

  useFrame((state) => {
    if (groupRef.current) {
      const x = basePosition[0] + ((state.clock.elapsedTime * speed) % range) - range / 2;
      groupRef.current.position.x = x;
    }
  });

  return (
    <group ref={groupRef} position={basePosition}>
      <RoundedBox args={[0.42, 0.16, 0.22]} radius={0.04}>
        <meshStandardMaterial color={color} />
      </RoundedBox>
      <RoundedBox args={[0.18, 0.1, 0.2]} position={[0.05, 0.1, 0]} radius={0.03}>
        <meshStandardMaterial color="#cbd5e1" />
      </RoundedBox>
    </group>
  );
};

const Boat = ({ basePosition, color = '#f8fafc', bobOffset = 0 }) => {
  const groupRef = useRef();

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.position.y = basePosition[1] + Math.sin(state.clock.elapsedTime * 1.2 + bobOffset) * 0.05;
      groupRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.9 + bobOffset) * 0.05;
    }
  });

  return (
    <group ref={groupRef} position={basePosition}>
      <mesh>
        <boxGeometry args={[0.7, 0.08, 0.22]} />
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh position={[0, 0.35, 0]}>
        <boxGeometry args={[0.03, 0.6, 0.03]} />
        <meshStandardMaterial color="#475569" />
      </mesh>
      <mesh position={[0.12, 0.28, 0]} rotation={[0, 0, 0.2]}>
        <planeGeometry args={[0.35, 0.3]} />
        <meshStandardMaterial color="#fef3c7" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
};

const Lantern = ({ basePosition, phase = 0 }) => {
  const ref = useRef();

  useFrame((state) => {
    if (ref.current) {
      ref.current.position.y = basePosition[1] + Math.sin(state.clock.elapsedTime * 0.9 + phase) * 0.08;
    }
  });

  return (
    <group ref={ref} position={basePosition}>
      <mesh>
        <sphereGeometry args={[0.1, 12, 12]} />
        <meshStandardMaterial color="#fb923c" emissive="#fb923c" emissiveIntensity={0.8} />
      </mesh>
      <pointLight color="#fb923c" intensity={0.4} distance={1.5} />
    </group>
  );
};

const ThailandTemple = ({ position }) => (
  <group position={position}>
    <mesh position={[0, 0.25, 0]}>
      <boxGeometry args={[1.2, 0.5, 0.6]} />
      <meshStandardMaterial color="#7c2d12" />
    </mesh>
    <mesh position={[0, 0.7, 0]} rotation={[0, 0, 0]}>
      <coneGeometry args={[0.75, 0.6, 4]} />
      <meshStandardMaterial color="#fbbf24" />
    </mesh>
    <mesh position={[0, 1.1, 0]}>
      <coneGeometry args={[0.2, 0.45, 4]} />
      <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.2} />
    </mesh>
  </group>
);

const HollywoodSign = ({ position }) => (
  <group position={position}>
    {['H', 'O', 'L', 'L', 'Y', 'W', 'O', 'O', 'D'].map((letter, index) => (
      <Text
        key={`${letter}-${index}`}
        position={[index * 0.32, 0, 0]}
        fontSize={0.22}
        color="#f8fafc"
        anchorX="center"
        anchorY="middle"
      >
        {letter}
      </Text>
    ))}
  </group>
);

const FerrisWheel = ({ position }) => {
  const ref = useRef();

  useFrame((state) => {
    if (ref.current) {
      ref.current.rotation.z = state.clock.elapsedTime * 0.18;
    }
  });

  return (
    <group position={position}>
      <group ref={ref}>
        <mesh>
          <torusGeometry args={[0.8, 0.04, 12, 48]} />
          <meshStandardMaterial color="#e2e8f0" />
        </mesh>
        {Array.from({ length: 8 }).map((_, index) => {
          const angle = (index / 8) * Math.PI * 2;
          return (
            <group key={index} rotation={[0, 0, angle]}>
              <mesh position={[0.55, 0, 0]}>
                <boxGeometry args={[0.42, 0.02, 0.02]} />
                <meshStandardMaterial color="#94a3b8" />
              </mesh>
              <mesh position={[0.86, 0, 0]}>
                <boxGeometry args={[0.12, 0.12, 0.12]} />
                <meshStandardMaterial color={index % 2 === 0 ? '#f472b6' : '#38bdf8'} emissive={index % 2 === 0 ? '#f472b6' : '#38bdf8'} emissiveIntensity={0.35} />
              </mesh>
            </group>
          );
        })}
      </group>
      <mesh position={[-0.32, -1, 0]} rotation={[0, 0, 0.2]}>
        <boxGeometry args={[0.08, 2, 0.08]} />
        <meshStandardMaterial color="#64748b" />
      </mesh>
      <mesh position={[0.32, -1, 0]} rotation={[0, 0, -0.2]}>
        <boxGeometry args={[0.08, 2, 0.08]} />
        <meshStandardMaterial color="#64748b" />
      </mesh>
    </group>
  );
};

const ArtDecoHotel = ({ position, color = '#f472b6', accent = '#67e8f9' }) => (
  <group position={position}>
    <RoundedBox args={[1.1, 2.8, 0.45]} radius={0.03}>
      <meshStandardMaterial color={color} />
    </RoundedBox>
    <RoundedBox args={[0.8, 0.24, 0.5]} position={[0, 1.6, 0.03]} radius={0.03}>
      <meshStandardMaterial color={accent} emissive={accent} emissiveIntensity={0.25} />
    </RoundedBox>
    <RoundedBox args={[0.46, 0.28, 0.5]} position={[0, 2.05, 0.03]} radius={0.03}>
      <meshStandardMaterial color="#fef3c7" />
    </RoundedBox>
  </group>
);

const LifeguardTower = ({ position }) => (
  <group position={position}>
    <RoundedBox args={[0.6, 0.28, 0.55]} position={[0, 0.7, 0]} radius={0.03}>
      <meshStandardMaterial color="#fef3c7" />
    </RoundedBox>
    <mesh position={[0, 1.05, 0.25]}>
      <planeGeometry args={[0.42, 0.16]} />
      <meshStandardMaterial color="#f97316" emissive="#f97316" emissiveIntensity={0.2} />
    </mesh>
    {[[-0.22, 0.3, -0.18], [0.22, 0.3, -0.18], [-0.22, 0.3, 0.18], [0.22, 0.3, 0.18]].map((pos, index) => (
      <mesh key={index} position={pos}>
        <cylinderGeometry args={[0.03, 0.03, 0.6]} />
        <meshStandardMaterial color="#92400e" />
      </mesh>
    ))}
  </group>
);

const LongtailBoat = ({ basePosition, color = '#8b5a2b', stripe = '#f97316', bobOffset = 0 }) => {
  const groupRef = useRef();

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.position.y = basePosition[1] + Math.sin(state.clock.elapsedTime * 1.1 + bobOffset) * 0.05;
      groupRef.current.rotation.z = Math.sin(state.clock.elapsedTime * 0.7 + bobOffset) * 0.04;
    }
  });

  return (
    <group ref={groupRef} position={basePosition}>
      <mesh>
        <capsuleGeometry args={[0.12, 0.9, 4, 12]} rotation={[0, 0, Math.PI / 2]} />
        <meshStandardMaterial color={color} />
      </mesh>
      <mesh position={[0.05, 0.12, 0]}>
        <boxGeometry args={[0.52, 0.05, 0.1]} />
        <meshStandardMaterial color={stripe} />
      </mesh>
      <mesh position={[0.42, 0.42, 0]}>
        <boxGeometry args={[0.03, 0.7, 0.03]} />
        <meshStandardMaterial color="#475569" />
      </mesh>
      <mesh position={[0.24, 0.34, 0]} rotation={[0, 0, 0.28]}>
        <planeGeometry args={[0.34, 0.26]} />
        <meshStandardMaterial color="#fef3c7" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
};

const LABackdrop = () => (
  <group position={[0, 0, -5.3]}>
    {[-5.2, -3.8, -2.4, -1.1, 0.5, 2.0, 3.6, 5.2].map((x, index) => (
      <mesh key={`hill-${x}`} position={[x, 1.2 + (index % 3) * 0.18, -1.7]}>
        <coneGeometry args={[1.6 + (index % 2) * 0.35, 1.1 + (index % 3) * 0.18, 5]} />
        <meshStandardMaterial color={index % 2 === 0 ? '#78716c' : '#57534e'} />
      </mesh>
    ))}
    <HollywoodSign position={[-1.75, 2.2, -0.3]} />
    <FerrisWheel position={[5.35, 2.1, 0.15]} />
    <mesh position={[0, 0.02, 1.3]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[18, 2.4]} />
      <meshStandardMaterial color="#52525b" />
    </mesh>
    <mesh position={[0, 0.03, 1.3]}>
      <boxGeometry args={[18, 0.02, 0.5]} />
      <meshStandardMaterial color="#374151" />
    </mesh>
    <Car basePosition={[-2, 0.12, 1.45]} color="#ef4444" speed={0.9} />
    <Car basePosition={[2, 0.12, 1.15]} color="#38bdf8" speed={0.65} range={4.2} />
    {[-5.0, -3.4, -1.8, -0.2, 1.4, 3.0, 4.6].map((x, index) => (
      <SkylineBuilding
        key={x}
        position={[x, 2 + (index % 3) * 0.2, -0.4 - (index % 2) * 0.2]}
        size={[0.55 + (index % 2) * 0.15, 1.6 + (index % 4) * 0.28, 0.45]}
        color={index % 2 === 0 ? '#1f2937' : '#334155'}
        glow={index % 2 === 0 ? '#fbbf24' : '#38bdf8'}
      />
    ))}
    <PalmTree position={[-5.5, 0, 2.3]} swayOffset={0.3} />
    <PalmTree position={[5.3, 0, 2.0]} swayOffset={1.1} />
  </group>
);

const MiamiBackdrop = () => (
  <group position={[0, 0, -5.4]}>
    <mesh position={[0, 0.01, 0.2]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[18, 4.2]} />
      <meshStandardMaterial color="#38bdf8" transparent opacity={0.9} />
    </mesh>
    <mesh position={[0, 0.03, 2.35]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[18, 1.4]} />
      <meshStandardMaterial color="#f5d0a5" />
    </mesh>
    <ArtDecoHotel position={[-4.1, 1.8, 0.5]} color="#f472b6" accent="#67e8f9" />
    <ArtDecoHotel position={[-2.5, 1.6, 0.55]} color="#38bdf8" accent="#f472b6" />
    <ArtDecoHotel position={[-0.9, 1.72, 0.6]} color="#fde68a" accent="#38bdf8" />
    <ArtDecoHotel position={[0.9, 1.68, 0.6]} color="#f9a8d4" accent="#67e8f9" />
    <SkylineBuilding position={[3.0, 2.15, 0.2]} size={[0.7, 2.2, 0.5]} color="#0f172a" glow="#67e8f9" />
    <SkylineBuilding position={[4.4, 2.25, 0.1]} size={[0.65, 2.4, 0.45]} color="#1e293b" glow="#f472b6" />
    <LifeguardTower position={[-5.0, 0, 2.45]} />
    <Boat basePosition={[-2.6, 0.08, 0.2]} color="#f8fafc" bobOffset={0.4} />
    <Boat basePosition={[2.1, 0.08, 0.55]} color="#fde68a" bobOffset={1.1} />
    <PalmTree position={[-5.3, 0, 2.7]} swayOffset={0.8} />
    <PalmTree position={[5.2, 0, 2.55]} swayOffset={1.9} />
  </group>
);

const ThailandBackdrop = () => (
  <group position={[0, 0, -5.5]}>
    <mesh position={[0, 0.01, 0.1]} rotation={[-Math.PI / 2, 0, 0]}>
      <planeGeometry args={[18, 4.8]} />
      <meshStandardMaterial color="#0ea5e9" transparent opacity={0.95} />
    </mesh>
    {[-5.7, -4.0, -1.4, 2.4, 5.2].map((x, index) => (
      <mesh key={x} position={[x, 1.3 + (index % 2) * 0.25, -1.1 + (index % 3) * 0.25]}>
        <coneGeometry args={[1.2 + (index % 2) * 0.4, 2.2 + (index % 3) * 0.35, 5]} />
        <meshStandardMaterial color={index % 2 === 0 ? '#166534' : '#15803d'} />
      </mesh>
    ))}
    <ThailandTemple position={[0.6, 0, 1.1]} />
    <LongtailBoat basePosition={[-2.4, 0.08, 0.1]} color="#8b5a2b" stripe="#f59e0b" bobOffset={0.3} />
    <LongtailBoat basePosition={[2.8, 0.06, 0.45]} color="#7c2d12" stripe="#fbbf24" bobOffset={1.5} />
    <Lantern basePosition={[-1.8, 2.6, 1.3]} phase={0.2} />
    <Lantern basePosition={[0.5, 3.0, 1.0]} phase={1.1} />
    <Lantern basePosition={[2.4, 2.7, 1.4]} phase={1.8} />
    <PalmTree position={[-4.9, 0, 2.2]} swayOffset={0.5} />
    <PalmTree position={[4.8, 0, 2.35]} swayOffset={1.4} />
  </group>
);

const ScenicBackdrop = ({ sceneryTheme = 'losAngeles' }) => {
  switch (sceneryTheme) {
    case 'miami':
      return <MiamiBackdrop />;
    case 'thailand':
      return <ThailandBackdrop />;
    default:
      return <LABackdrop />;
  }
};

// ============================================
// 3D CHARACTER SYSTEM - BODY TYPES
// ============================================

// Human body with customizable skin and outfit
const HumanBody = ({ skinColor = '#f5d0b5', outfitColor, hairColor = '#1f2937', hairStyle = 'short' }) => {
  return (
    <group>
      {/* Torso */}
      <mesh position={[0, 0.42, 0]}>
        <capsuleGeometry args={[0.1, 0.22, 8, 16]} />
        <meshStandardMaterial color={outfitColor} />
      </mesh>
      {/* Head */}
      <mesh position={[0, 0.75, 0]}>
        <sphereGeometry args={[0.11, 16, 16]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.04, 0.77, 0.09]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.04, 0.77, 0.09]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[-0.04, 0.77, 0.105]}>
        <sphereGeometry args={[0.01, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      <mesh position={[0.04, 0.77, 0.105]}>
        <sphereGeometry args={[0.01, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      {/* Hair */}
      {hairStyle === 'short' && (
        <mesh position={[0, 0.84, -0.02]}>
          <sphereGeometry args={[0.1, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
          <meshStandardMaterial color={hairColor} />
        </mesh>
      )}
      {hairStyle === 'long' && (
        <group>
          <mesh position={[0, 0.84, -0.02]}>
            <sphereGeometry args={[0.1, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={hairColor} />
          </mesh>
          <mesh position={[0, 0.7, -0.08]}>
            <capsuleGeometry args={[0.08, 0.15, 8, 8]} />
            <meshStandardMaterial color={hairColor} />
          </mesh>
        </group>
      )}
      {/* Arms */}
      <mesh position={[-0.15, 0.4, 0]} rotation={[0, 0, 0.3]}>
        <capsuleGeometry args={[0.03, 0.15, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      <mesh position={[0.15, 0.4, 0]} rotation={[0, 0, -0.3]}>
        <capsuleGeometry args={[0.03, 0.15, 8, 8]} />
        <meshStandardMaterial color={skinColor} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.05, 0.12, 0]}>
        <capsuleGeometry args={[0.04, 0.12, 8, 8]} />
        <meshStandardMaterial color="#374151" />
      </mesh>
      <mesh position={[0.05, 0.12, 0]}>
        <capsuleGeometry args={[0.04, 0.12, 8, 8]} />
        <meshStandardMaterial color="#374151" />
      </mesh>
    </group>
  );
};

// Dog character body
const DogBody = ({ furColor = '#8B4513', outfitColor }) => {
  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.35, 0]} rotation={[0.2, 0, 0]}>
        <capsuleGeometry args={[0.12, 0.18, 8, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Outfit/vest on body */}
      {outfitColor && (
        <mesh position={[0, 0.38, 0.01]} rotation={[0.2, 0, 0]}>
          <capsuleGeometry args={[0.125, 0.12, 8, 16]} />
          <meshStandardMaterial color={outfitColor} />
        </mesh>
      )}
      {/* Head */}
      <mesh position={[0, 0.65, 0.05]}>
        <sphereGeometry args={[0.13, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Snout */}
      <mesh position={[0, 0.6, 0.16]}>
        <capsuleGeometry args={[0.05, 0.06, 8, 8]} rotation={[Math.PI / 2, 0, 0]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.62, 0.22]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.05, 0.7, 0.12]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[0.05, 0.7, 0.12]}>
        <sphereGeometry args={[0.03, 8, 8]} />
        <meshStandardMaterial color="#ffffff" />
      </mesh>
      <mesh position={[-0.05, 0.7, 0.14]}>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      <mesh position={[0.05, 0.7, 0.14]}>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      {/* Ears */}
      <mesh position={[-0.1, 0.78, 0]} rotation={[0, 0, -0.5]}>
        <capsuleGeometry args={[0.03, 0.08, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.1, 0.78, 0]} rotation={[0, 0, 0.5]}>
        <capsuleGeometry args={[0.03, 0.08, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Tail */}
      <mesh position={[0, 0.35, -0.18]} rotation={[-0.8, 0, 0]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Front legs */}
      <mesh position={[-0.08, 0.15, 0.05]}>
        <capsuleGeometry args={[0.035, 0.12, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.08, 0.15, 0.05]}>
        <capsuleGeometry args={[0.035, 0.12, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Back legs */}
      <mesh position={[-0.08, 0.15, -0.08]}>
        <capsuleGeometry args={[0.035, 0.12, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.08, 0.15, -0.08]}>
        <capsuleGeometry args={[0.035, 0.12, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
    </group>
  );
};

// Cat character body
const CatBody = ({ furColor = '#808080', outfitColor }) => {
  return (
    <group>
      {/* Body */}
      <mesh position={[0, 0.32, 0]}>
        <capsuleGeometry args={[0.09, 0.16, 8, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Outfit collar */}
      {outfitColor && (
        <mesh position={[0, 0.45, 0]}>
          <torusGeometry args={[0.08, 0.015, 8, 16]} />
          <meshStandardMaterial color={outfitColor} />
        </mesh>
      )}
      {/* Head */}
      <mesh position={[0, 0.58, 0.02]}>
        <sphereGeometry args={[0.1, 16, 16]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Ears - triangular */}
      <mesh position={[-0.07, 0.72, 0]} rotation={[0, 0, -0.3]}>
        <coneGeometry args={[0.04, 0.08, 3]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.07, 0.72, 0]} rotation={[0, 0, 0.3]}>
        <coneGeometry args={[0.04, 0.08, 3]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Inner ears */}
      <mesh position={[-0.07, 0.71, 0.01]} rotation={[0, 0, -0.3]}>
        <coneGeometry args={[0.025, 0.05, 3]} />
        <meshStandardMaterial color="#fca5a5" />
      </mesh>
      <mesh position={[0.07, 0.71, 0.01]} rotation={[0, 0, 0.3]}>
        <coneGeometry args={[0.025, 0.05, 3]} />
        <meshStandardMaterial color="#fca5a5" />
      </mesh>
      {/* Eyes */}
      <mesh position={[-0.035, 0.6, 0.08]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#22c55e" />
      </mesh>
      <mesh position={[0.035, 0.6, 0.08]}>
        <sphereGeometry args={[0.025, 8, 8]} />
        <meshStandardMaterial color="#22c55e" />
      </mesh>
      <mesh position={[-0.035, 0.6, 0.1]}>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      <mesh position={[0.035, 0.6, 0.1]}>
        <sphereGeometry args={[0.012, 8, 8]} />
        <meshStandardMaterial color="#1f2937" />
      </mesh>
      {/* Nose */}
      <mesh position={[0, 0.55, 0.1]}>
        <sphereGeometry args={[0.015, 8, 8]} />
        <meshStandardMaterial color="#fca5a5" />
      </mesh>
      {/* Whiskers */}
      {[-0.04, 0.04].map((x, i) => (
        <group key={i} position={[x, 0.54, 0.09]}>
          <mesh rotation={[0, 0, x > 0 ? -0.2 : 0.2]}>
            <cylinderGeometry args={[0.002, 0.002, 0.08]} rotation={[0, 0, Math.PI / 2]} />
            <meshStandardMaterial color="#e5e7eb" />
          </mesh>
        </group>
      ))}
      {/* Tail */}
      <mesh position={[0, 0.35, -0.15]} rotation={[-1, 0, 0]}>
        <capsuleGeometry args={[0.02, 0.15, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.05, 0.12, 0.02]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.05, 0.12, 0.02]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[-0.05, 0.12, -0.05]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
      <mesh position={[0.05, 0.12, -0.05]}>
        <capsuleGeometry args={[0.025, 0.1, 8, 8]} />
        <meshStandardMaterial color={furColor} />
      </mesh>
    </group>
  );
};

// Robot character body
const RobotBody = ({ bodyColor = '#64748b', accentColor = '#3b82f6' }) => {
  return (
    <group>
      {/* Torso */}
      <RoundedBox args={[0.22, 0.28, 0.15]} position={[0, 0.38, 0]} radius={0.02}>
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </RoundedBox>
      {/* Chest light */}
      <mesh position={[0, 0.4, 0.08]}>
        <circleGeometry args={[0.03, 16]} />
        <meshStandardMaterial color={accentColor} emissive={accentColor} emissiveIntensity={1} />
      </mesh>
      {/* Head */}
      <RoundedBox args={[0.18, 0.15, 0.15]} position={[0, 0.62, 0]} radius={0.02}>
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </RoundedBox>
      {/* Visor/Eyes */}
      <mesh position={[0, 0.63, 0.076]}>
        <boxGeometry args={[0.14, 0.04, 0.01]} />
        <meshStandardMaterial color={accentColor} emissive={accentColor} emissiveIntensity={0.5} />
      </mesh>
      {/* Antenna */}
      <mesh position={[0, 0.75, 0]}>
        <cylinderGeometry args={[0.01, 0.01, 0.08]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0, 0.8, 0]}>
        <sphereGeometry args={[0.02, 8, 8]} />
        <meshStandardMaterial color={accentColor} emissive={accentColor} emissiveIntensity={1} />
      </mesh>
      {/* Arms */}
      <mesh position={[-0.16, 0.4, 0]}>
        <capsuleGeometry args={[0.03, 0.15, 8, 8]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0.16, 0.4, 0]}>
        <capsuleGeometry args={[0.03, 0.15, 8, 8]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Hands */}
      <mesh position={[-0.16, 0.25, 0]}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color={accentColor} metalness={0.5} roughness={0.3} />
      </mesh>
      <mesh position={[0.16, 0.25, 0]}>
        <sphereGeometry args={[0.035, 8, 8]} />
        <meshStandardMaterial color={accentColor} metalness={0.5} roughness={0.3} />
      </mesh>
      {/* Legs */}
      <mesh position={[-0.06, 0.12, 0]}>
        <capsuleGeometry args={[0.035, 0.1, 8, 8]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      <mesh position={[0.06, 0.12, 0]}>
        <capsuleGeometry args={[0.035, 0.1, 8, 8]} />
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </mesh>
      {/* Feet */}
      <RoundedBox args={[0.06, 0.03, 0.08]} position={[-0.06, 0.02, 0.01]} radius={0.01}>
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </RoundedBox>
      <RoundedBox args={[0.06, 0.03, 0.08]} position={[0.06, 0.02, 0.01]} radius={0.01}>
        <meshStandardMaterial color={bodyColor} metalness={0.8} roughness={0.2} />
      </RoundedBox>
    </group>
  );
};

// ============================================
// 3D CHARACTER ACCESSORIES
// ============================================

// Glasses accessory
const Glasses = ({ type = 'round', color = '#1f2937', bodyType = 'human' }) => {
  const yOffset = bodyType === 'dog' ? 0.7 : bodyType === 'cat' ? 0.6 : 0.77;
  const zOffset = bodyType === 'dog' ? 0.14 : bodyType === 'cat' ? 0.1 : 0.11;

  if (type === 'none') return null;

  return (
    <group position={[0, yOffset, zOffset]}>
      {type === 'round' && (
        <>
          <mesh position={[-0.04, 0, 0]}>
            <torusGeometry args={[0.025, 0.004, 8, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0.04, 0, 0]}>
            <torusGeometry args={[0.025, 0.004, 8, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, -0.005]}>
            <boxGeometry args={[0.03, 0.004, 0.004]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'square' && (
        <>
          <mesh position={[-0.04, 0, 0]}>
            <boxGeometry args={[0.05, 0.035, 0.005]} />
            <meshStandardMaterial color={color} transparent opacity={0.3} />
          </mesh>
          <mesh position={[0.04, 0, 0]}>
            <boxGeometry args={[0.05, 0.035, 0.005]} />
            <meshStandardMaterial color={color} transparent opacity={0.3} />
          </mesh>
          {/* Frame */}
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.12, 0.004, 0.004]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'sunglasses' && (
        <>
          <mesh position={[-0.04, 0, 0]}>
            <boxGeometry args={[0.055, 0.03, 0.008]} />
            <meshStandardMaterial color="#1f2937" />
          </mesh>
          <mesh position={[0.04, 0, 0]}>
            <boxGeometry args={[0.055, 0.03, 0.008]} />
            <meshStandardMaterial color="#1f2937" />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.025, 0.006, 0.006]} />
            <meshStandardMaterial color="#1f2937" />
          </mesh>
        </>
      )}
    </group>
  );
};

// Hat accessory
const Hat = ({ type = 'none', color = '#1f2937', bodyType = 'human' }) => {
  const yOffset = bodyType === 'dog' ? 0.82 : bodyType === 'cat' ? 0.72 : 0.88;

  if (type === 'none') return null;

  return (
    <group position={[0, yOffset, 0]}>
      {type === 'tophat' && (
        <>
          <mesh position={[0, 0.06, 0]}>
            <cylinderGeometry args={[0.06, 0.06, 0.12, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0]}>
            <cylinderGeometry args={[0.1, 0.1, 0.015, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Band */}
          <mesh position={[0, 0.02, 0]}>
            <cylinderGeometry args={[0.062, 0.062, 0.02, 16]} />
            <meshStandardMaterial color="#dc2626" />
          </mesh>
        </>
      )}
      {type === 'cap' && (
        <>
          <mesh position={[0, 0.02, 0]}>
            <sphereGeometry args={[0.1, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Brim */}
          <mesh position={[0, 0, 0.08]} rotation={[-0.3, 0, 0]}>
            <boxGeometry args={[0.12, 0.01, 0.08]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'beanie' && (
        <>
          <mesh position={[0, 0.03, 0]}>
            <sphereGeometry args={[0.11, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Fold */}
          <mesh position={[0, 0, 0]}>
            <torusGeometry args={[0.1, 0.02, 8, 16]} rotation={[Math.PI / 2, 0, 0]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Pom pom */}
          <mesh position={[0, 0.12, 0]}>
            <sphereGeometry args={[0.03, 8, 8]} />
            <meshStandardMaterial color="#ffffff" />
          </mesh>
        </>
      )}
      {type === 'crown' && (
        <>
          <mesh position={[0, 0.03, 0]}>
            <cylinderGeometry args={[0.09, 0.1, 0.06, 6]} />
            <meshStandardMaterial color="#fbbf24" metalness={0.8} roughness={0.2} />
          </mesh>
          {/* Crown points */}
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <mesh key={i} position={[Math.cos((i * Math.PI) / 3) * 0.08, 0.08, Math.sin((i * Math.PI) / 3) * 0.08]}>
              <coneGeometry args={[0.02, 0.04, 4]} />
              <meshStandardMaterial color="#fbbf24" metalness={0.8} roughness={0.2} />
            </mesh>
          ))}
          {/* Jewels */}
          <mesh position={[0, 0.03, 0.095]}>
            <sphereGeometry args={[0.015, 8, 8]} />
            <meshStandardMaterial color="#dc2626" emissive="#dc2626" emissiveIntensity={0.3} />
          </mesh>
        </>
      )}
      {type === 'cowboy' && (
        <>
          <mesh position={[0, 0.04, 0]}>
            <sphereGeometry args={[0.09, 16, 16, 0, Math.PI * 2, 0, Math.PI / 2]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Wide brim */}
          <mesh position={[0, 0, 0]} rotation={[0.1, 0, 0]}>
            <cylinderGeometry args={[0.15, 0.15, 0.015, 16]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
    </group>
  );
};

// Tie accessory (for humans/bipeds)
const Tie = ({ type = 'none', color = '#dc2626' }) => {
  if (type === 'none') return null;

  return (
    <group position={[0, 0.48, 0.1]}>
      {type === 'necktie' && (
        <>
          {/* Knot */}
          <mesh position={[0, 0, 0]}>
            <boxGeometry args={[0.03, 0.02, 0.015]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Tie body */}
          <mesh position={[0, -0.08, 0]} rotation={[0.1, 0, 0]}>
            <boxGeometry args={[0.04, 0.12, 0.01]} />
            <meshStandardMaterial color={color} />
          </mesh>
          {/* Tie point */}
          <mesh position={[0, -0.16, 0.005]} rotation={[0.1, 0, Math.PI / 4]}>
            <boxGeometry args={[0.03, 0.03, 0.01]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'bowtie' && (
        <>
          <mesh position={[-0.025, 0, 0]} rotation={[0, 0, 0.3]}>
            <boxGeometry args={[0.04, 0.025, 0.015]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0.025, 0, 0]} rotation={[0, 0, -0.3]}>
            <boxGeometry args={[0.04, 0.025, 0.015]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0, 0, 0.005]}>
            <boxGeometry args={[0.015, 0.02, 0.01]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
    </group>
  );
};

// Shoes accessory
const Shoes = ({ type = 'none', color = '#1f2937', bodyType = 'human' }) => {
  if (type === 'none' || bodyType === 'dog' || bodyType === 'cat') return null;

  return (
    <group position={[0, 0.02, 0]}>
      {type === 'formal' && (
        <>
          <RoundedBox args={[0.055, 0.03, 0.1]} position={[-0.05, 0, 0.02]} radius={0.01}>
            <meshStandardMaterial color={color} />
          </RoundedBox>
          <RoundedBox args={[0.055, 0.03, 0.1]} position={[0.05, 0, 0.02]} radius={0.01}>
            <meshStandardMaterial color={color} />
          </RoundedBox>
        </>
      )}
      {type === 'sneakers' && (
        <>
          <RoundedBox args={[0.06, 0.04, 0.1]} position={[-0.05, 0, 0.02]} radius={0.015}>
            <meshStandardMaterial color="#ffffff" />
          </RoundedBox>
          <RoundedBox args={[0.06, 0.04, 0.1]} position={[0.05, 0, 0.02]} radius={0.015}>
            <meshStandardMaterial color="#ffffff" />
          </RoundedBox>
          {/* Accent stripe */}
          <mesh position={[-0.05, 0.01, 0.04]}>
            <boxGeometry args={[0.065, 0.015, 0.04]} />
            <meshStandardMaterial color={color} />
          </mesh>
          <mesh position={[0.05, 0.01, 0.04]}>
            <boxGeometry args={[0.065, 0.015, 0.04]} />
            <meshStandardMaterial color={color} />
          </mesh>
        </>
      )}
      {type === 'boots' && (
        <>
          <RoundedBox args={[0.055, 0.08, 0.09]} position={[-0.05, 0.02, 0.01]} radius={0.01}>
            <meshStandardMaterial color={color} />
          </RoundedBox>
          <RoundedBox args={[0.055, 0.08, 0.09]} position={[0.05, 0.02, 0.01]} radius={0.01}>
            <meshStandardMaterial color={color} />
          </RoundedBox>
        </>
      )}
    </group>
  );
};

// ============================================
// MAIN AGENT CHARACTER COMPONENT
// ============================================

const AgentCharacter = ({ position, targetPosition = position, agent, isActive, palette, onClick, activityLabel = null }) => {
  const meshRef = useRef();
  const [hovered, setHovered] = useState(false);

  // Get avatar3D config with defaults
  const avatar3D = agent.avatar3D || {};
  const bodyType = avatar3D.bodyType || 'human';
  const accessories = avatar3D.accessories || {};

  useFrame((state) => {
    if (meshRef.current) {
      meshRef.current.position.x = THREE.MathUtils.lerp(meshRef.current.position.x, targetPosition[0], 0.03);
      meshRef.current.position.z = THREE.MathUtils.lerp(meshRef.current.position.z, targetPosition[2], 0.03);
      meshRef.current.position.y = THREE.MathUtils.lerp(
        meshRef.current.position.y,
        targetPosition[1] + Math.sin(state.clock.elapsedTime * 2) * (isActive ? 0.05 : 0.03),
        0.05
      );

      const dx = targetPosition[0] - meshRef.current.position.x;
      const dz = targetPosition[2] - meshRef.current.position.z;
      if (Math.abs(dx) + Math.abs(dz) > 0.02) {
        const targetRotation = Math.atan2(dx, dz);
        meshRef.current.rotation.y = THREE.MathUtils.lerp(meshRef.current.rotation.y, targetRotation, 0.08);
      }
    }
  });

  // Render appropriate body type
  const renderBody = () => {
    switch (bodyType) {
      case 'dog':
        return <DogBody furColor={avatar3D.furColor || '#8B4513'} outfitColor={palette.primary} />;
      case 'cat':
        return <CatBody furColor={avatar3D.furColor || '#808080'} outfitColor={palette.primary} />;
      case 'robot':
        return <RobotBody bodyColor={avatar3D.bodyColor || '#64748b'} accentColor={palette.primary} />;
      case 'human':
      default:
        return (
          <HumanBody
            skinColor={avatar3D.skinColor || '#f5d0b5'}
            outfitColor={palette.primary}
            hairColor={avatar3D.hairColor || '#1f2937'}
            hairStyle={avatar3D.hairStyle || 'short'}
          />
        );
    }
  };

  // Get head height for positioning accessories
  const headHeight = bodyType === 'dog' ? 0.65 : bodyType === 'cat' ? 0.58 : bodyType === 'robot' ? 0.62 : 0.75;

  return (
    <group
      ref={meshRef}
      position={position}
      onClick={onClick}
      onPointerOver={() => setHovered(true)}
      onPointerOut={() => setHovered(false)}
    >
      {/* Body */}
      {renderBody()}

      {/* Accessories */}
      <Glasses type={accessories.glasses} color={accessories.glassesColor} bodyType={bodyType} />
      <Hat type={accessories.hat} color={accessories.hatColor} bodyType={bodyType} />
      {bodyType === 'human' && <Tie type={accessories.tie} color={accessories.tieColor} />}
      <Shoes type={accessories.shoes} color={accessories.shoesColor} bodyType={bodyType} />

      {activityLabel && (
        <group position={[0, headHeight + 0.38, 0]}>
          <RoundedBox args={[0.95, 0.18, 0.02]} radius={0.03}>
            <meshStandardMaterial color="#ffffff" />
          </RoundedBox>
          <Text
            position={[0, 0, 0.02]}
            fontSize={0.05}
            color="#0f172a"
            anchorX="center"
            anchorY="middle"
          >
            {activityLabel}
          </Text>
        </group>
      )}

      {/* Name tag */}
      <group position={[0, -0.1, 0]}>
        <RoundedBox args={[0.5, 0.15, 0.02]} radius={0.02}>
          <meshStandardMaterial color={isActive ? "#22c55e" : "#0f172a"} />
        </RoundedBox>
        <Text
          position={[0, 0, 0.02]}
          fontSize={0.06}
          color="white"
          anchorX="center"
          anchorY="middle"
        >
          {agent.name}
        </Text>
      </group>

      {/* Status indicator */}
      {isActive && (
        <pointLight position={[0, 0.5, 0.3]} color="#22c55e" intensity={0.3} distance={1} />
      )}

      {/* Hover highlight */}
      {hovered && (
        <mesh position={[0, 0.01, 0]} rotation={[-Math.PI / 2, 0, 0]}>
          <ringGeometry args={[0.3, 0.35, 32]} />
          <meshBasicMaterial color="#ff4d4d" transparent opacity={0.5} />
        </mesh>
      )}
    </group>
  );
};

// ============================================
// FLOOR & WALLS
// ============================================

const Room = ({ width = 12, depth = 8, height = 3, appearance = {} }) => {
  const floorTheme = FLOOR_THEMES[appearance.floorType] || FLOOR_THEMES.wood;
  const wallTheme = WALL_THEMES[appearance.wallTheme] || WALL_THEMES.warm;
  return (
    <group>
      {/* Floor */}
      <mesh rotation={[-Math.PI / 2, 0, 0]} position={[0, 0, 0]} receiveShadow>
        <planeGeometry args={[width, depth]} />
        <meshStandardMaterial color={floorTheme.floor} />
      </mesh>
      {/* Floor grid pattern */}
      <gridHelper args={[Math.max(width, depth), 20, floorTheme.gridMajor, floorTheme.gridMinor]} position={[0, 0.01, 0]} />

      {/* Back wall */}
      <mesh position={[0, height / 2, -depth / 2]} receiveShadow>
        <planeGeometry args={[width, height]} />
        <meshStandardMaterial color={wallTheme.back} side={THREE.DoubleSide} />
      </mesh>

      {/* Left wall */}
      <mesh position={[-width / 2, height / 2, 0]} rotation={[0, Math.PI / 2, 0]} receiveShadow>
        <planeGeometry args={[depth, height]} />
        <meshStandardMaterial color={wallTheme.left} side={THREE.DoubleSide} />
      </mesh>

      {/* Baseboard - back */}
      <mesh position={[0, 0.05, -depth / 2 + 0.02]}>
        <boxGeometry args={[width, 0.1, 0.04]} />
        <meshStandardMaterial color={wallTheme.trim} />
      </mesh>

      {/* Baseboard - left */}
      <mesh position={[-width / 2 + 0.02, 0.05, 0]} rotation={[0, Math.PI / 2, 0]}>
        <boxGeometry args={[depth, 0.1, 0.04]} />
        <meshStandardMaterial color={wallTheme.trim} />
      </mesh>
    </group>
  );
};

// ============================================
// MAIN 3D OFFICE SCENE
// ============================================

const OfficeScene = ({ agents, activeEvents, onAgentClick, officeName, layout }) => {
  const appearance = layout?.appearance || { floorType: layout?.floorType || 'wood', wallTheme: 'warm' };
  const sceneryTheme = appearance.sceneryTheme || 'losAngeles';
  const [agentStates, setAgentStates] = useState({});
  const agentPositions = useMemo(() => {
    const positions = [];
    const cols = Math.min(4, Math.max(2, Math.ceil(Math.sqrt(agents.length))));
    agents.forEach((agent, index) => {
      const col = index % cols;
      const row = Math.floor(index / cols);
      positions.push({
        desk: [-3 + col * 2.5, 0, -1 + row * 2.5],
        chair: [-3 + col * 2.5, 0, 0 + row * 2.5],
        agent: [-3 + col * 2.5, 0, 0.3 + row * 2.5],
      });
    });
    return positions;
  }, [agents]);

  const officeZones = useMemo(() => ({
    coffee: [-4.8, 0, 3.25],
    loungeA: [3.15, 0, 2.85],
    loungeB: [4.75, 0, 3.45],
    huddleA: [0.8, 0, 0.8],
    huddleB: [1.95, 0, 1.45],
    hallwayA: [-2.4, 0, 2.7],
    hallwayB: [2.4, 0, -2.8],
    windowA: [-4.8, 0, -2.3],
    windowB: [3.8, 0, -3.1],
  }), []);

  useEffect(() => {
    const initialState = {};
    agents.forEach((agent, index) => {
      initialState[agent.id] = {
        target: agentPositions[index]?.agent || [0, 0, 0],
        label: 'On Queue',
      };
    });
    setAgentStates(initialState);
  }, [agents, agentPositions]);

  useEffect(() => {
    if (agents.length === 0) return undefined;

    const applyMovementStep = () => {
      setAgentStates((current) => {
        const next = { ...current };
        const idleAgents = agents.filter((agent) => !activeEvents[agent.id]);
        const paired = new Set();

        agents.forEach((agent, index) => {
          if (activeEvents[agent.id]) {
            next[agent.id] = {
              target: agentPositions[index]?.agent || [0, 0, 0],
              label: 'Helping customer',
            };
          }
        });

        if (idleAgents.length >= 2 && Math.random() > 0.55) {
          const first = idleAgents[Math.floor(Math.random() * idleAgents.length)];
          const candidates = idleAgents.filter((agent) => agent.id !== first.id);
          const second = candidates[Math.floor(Math.random() * candidates.length)];
          if (first && second) {
            next[first.id] = { target: officeZones.huddleA, label: `Chatting with ${second.name}` };
            next[second.id] = { target: officeZones.huddleB, label: `Chatting with ${first.name}` };
            paired.add(first.id);
            paired.add(second.id);
          }
        }

        idleAgents.forEach((agent, index) => {
          if (paired.has(agent.id)) return;
          const home = agentPositions[agents.findIndex((entry) => entry.id === agent.id)]?.agent || [0, 0, 0];
          const movementOptions = [
            { target: home, label: 'On Queue' },
            { target: officeZones.coffee, label: 'Coffee run' },
            { target: index % 2 === 0 ? officeZones.loungeA : officeZones.loungeB, label: 'Taking a break' },
            { target: index % 2 === 0 ? officeZones.hallwayA : officeZones.hallwayB, label: 'Walking the floor' },
            { target: index % 2 === 0 ? officeZones.windowA : officeZones.windowB, label: 'Checking the view' },
            { target: home, label: 'Back at desk' },
          ];
          const weightedPool = [0, 0, 0, 0, 1, 2, 4, 5, 0, 0, 3];
          const chosenIndex = weightedPool[Math.floor(Math.random() * weightedPool.length)];
          next[agent.id] = movementOptions[chosenIndex];
        });

        return next;
      });
    };

    const kickoff = setTimeout(applyMovementStep, 2200);
    const interval = setInterval(() => {
      applyMovementStep();
    }, 6200);

    return () => {
      clearTimeout(kickoff);
      clearInterval(interval);
    };
  }, [activeEvents, agentPositions, agents, officeZones]);

  const getAgentPalette = (agent, index) => {
    const colors = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'];
    return {
      primary: agent.color || colors[index % colors.length],
    };
  };

  return (
    <>
      {/* Lighting */}
      <ambientLight intensity={0.4} />
      <directionalLight position={[5, 10, 5]} intensity={0.8} castShadow />
      <pointLight position={[-3, 2, 2]} intensity={0.3} color="#fef3c7" />

      {/* Room */}
      <Room width={12} depth={10} height={3.5} appearance={appearance} />
      <ScenicBackdrop sceneryTheme={sceneryTheme} />

      {/* Windows */}
      <Window3D position={[-5.9, 2, -2]} />
      <Window3D position={[-5.9, 2, 1]} />
      <Window3D position={[0, 2, -4.9]} />
      <Window3D position={[3, 2, -4.9]} />

      {/* Furniture */}
      <Sofa position={[4, 0, 3]} />
      <Plant position={[5.5, 0, 3.5]} large />
      <Plant position={[-5.5, 0, -3.5]} />
      <CoffeeMachine position={[-5, 0, 3]} />
      <ServerRack position={[5.5, 0, -3.5]} />
      <Rug position={[4, 0.01, 3]} width={3} depth={2} />

      {/* Desks, Chairs, and Agents */}
      {agents.map((agent, index) => {
        const pos = agentPositions[index];
        if (!pos) return null;
        const isActive = Boolean(activeEvents[agent.id]);
        const state = agentStates[agent.id] || { target: pos.agent, label: 'On Queue' };
        return (
          <group key={agent.id}>
            <Desk position={pos.desk} isActive={isActive} />
            <Chair position={pos.chair} color={getAgentPalette(agent, index).primary} />
            <AgentCharacter
              position={pos.agent}
              targetPosition={state.target}
              agent={agent}
              isActive={isActive}
              palette={getAgentPalette(agent, index)}
              activityLabel={state.label}
              onClick={() => onAgentClick?.(agent)}
            />
          </group>
        );
      })}

      {/* Office name sign */}
      <group position={[0, 2.8, -4.85]}>
        <RoundedBox args={[3, 0.4, 0.05]} radius={0.02}>
          <meshStandardMaterial color="#0f172a" />
        </RoundedBox>
        <Text
          position={[0, 0, 0.03]}
          fontSize={0.15}
          color="white"
          anchorX="center"
          anchorY="middle"
        >
          {officeName}
        </Text>
      </group>
    </>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const VirtualOffice3D = ({
  agents = [],
  activeEvents = {},
  officeName = 'AI Office',
  bannerTitle = null,
  layout = null,
  onAgentClick,
}) => {
  const appearance = layout?.appearance || { floorType: layout?.floorType || 'wood', wallTheme: 'warm', sceneryTheme: 'losAngeles' };
  const floorTheme = FLOOR_THEMES[appearance.floorType] || FLOOR_THEMES.wood;
  const scenery = SCENERY_THEMES[appearance.sceneryTheme] || SCENERY_THEMES.losAngeles;
  return (
    <div className="w-full h-[540px] rounded-2xl overflow-hidden border border-white/10 relative">
      <Canvas
        shadows
        camera={{
          position: [10, 12, 14],
          fov: 45,
          near: 0.1,
          far: 100,
        }}
        style={{ background: `linear-gradient(180deg, ${scenery.skyTop} 0%, ${scenery.skyBottom} 65%, ${floorTheme.skyBottom} 100%)` }}
      >
        <OfficeScene
          agents={agents}
          activeEvents={activeEvents}
          onAgentClick={onAgentClick}
          officeName={bannerTitle || officeName}
          layout={layout}
        />
        <OrbitControls
          enablePan={true}
          enableZoom={true}
          enableRotate={true}
          minPolarAngle={Math.PI / 6}
          maxPolarAngle={Math.PI / 2.2}
          minDistance={5}
          maxDistance={30}
          target={[0, 0, 0]}
        />
        <Environment preset="city" />
      </Canvas>

      {/* Controls hint */}
      <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-2 text-white/70 text-xs">
        <span>🖱️ Drag to rotate • Scroll to zoom • Right-click to pan</span>
      </div>
      <div className="absolute top-4 right-4 bg-black/45 backdrop-blur-sm rounded-lg px-3 py-2 text-white/80 text-xs">
        <span>Outside: {appearance.sceneryTheme === 'miami' ? 'Miami' : appearance.sceneryTheme === 'thailand' ? 'Thailand' : 'LA'}</span>
      </div>
    </div>
  );
};

VirtualOffice3D.propTypes = {
  agents: PropTypes.array,
  activeEvents: PropTypes.object,
  officeName: PropTypes.string,
  bannerTitle: PropTypes.string,
  layout: PropTypes.object,
  onAgentClick: PropTypes.func,
};

export default VirtualOffice3D;
